import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const badgeColors = {
  "مميز": "bg-[#ecc775] text-[#173269]",
  "جديد": "bg-green-500 text-white",
  "للبيع": "bg-[#173269] text-white",
  "متاح": "bg-blue-500 text-white",
};

const defaultProjects = [
  {
    id: "1", title: "فيلا فاخرة - حي النخيل", description: "فيلا عائلية راقية بتصميم عصري وموقع متميز وتشطيبات فاخرة",
    location: "جازان - حي النخيل", price: "يُحدد بعد الاستشارة", type: "فيلا للبيع", badge: "مميز",
    images: [
      "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200&q=80",
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1200&q=80",
    ],
  },
  {
    id: "2", title: "شقة راقية - طريق المدينة", description: "شقة واسعة بإطلالة رائعة وتشطيبات فاخرة",
    location: "جازان - طريق المدينة المنورة", price: "عائد استثماري مميز", type: "شقة للإيجار", badge: "جديد",
    images: [
      "https://images.pexels.com/photos/6970056/pexels-photo-6970056.jpeg",
      "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
      "https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg",
    ],
  },
  {
    id: "3", title: "مجمع سكني - حي المحمدية", description: "مجمع متكامل بمرافق حديثة وأمن دائم",
    location: "جازان - حي المحمدية", price: "يُحدد بعد الاستشارة", type: "مجمع سكني", badge: "للبيع",
    images: [
      "https://images.pexels.com/photos/16573669/pexels-photo-16573669.jpeg",
      "https://images.pexels.com/photos/1643389/pexels-photo-1643389.jpeg",
      "https://images.pexels.com/photos/2079234/pexels-photo-2079234.jpeg",
    ],
  },
  {
    id: "4", title: "وحدة استثمارية فاخرة", description: "وحدة عصرية مناسبة للاستثمار المربح",
    location: "جازان - وسط المدينة", price: "عائد مرتفع", type: "للإيجار", badge: "متاح",
    images: [
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=1200&q=80",
      "https://images.pexels.com/photos/1648768/pexels-photo-1648768.jpeg",
      "https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg",
    ],
  },
];

/* ── Individual Property Card with its own slider ── */
const PropertyCard = ({ item, index, whatsapp }) => {
  const [cardRef, cardInView] = useInView(0.15);
  const [activeImg, setActiveImg] = useState(0);
  const [fade, setFade] = useState(true);
  const touchStartX = useRef(null);
  const images = item.images?.length > 0 ? item.images : [item.image].filter(Boolean);

  const go = useCallback((dir) => {
    setFade(false);
    setTimeout(() => {
      setActiveImg(prev => (prev + dir + images.length) % images.length);
      setFade(true);
    }, 220);
  }, [images.length]);

  const goTo = (i) => {
    if (i === activeImg) return;
    setFade(false);
    setTimeout(() => { setActiveImg(i); setFade(true); }, 220);
  };

  // Auto-slide every 4 seconds
  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => go(1), 4000);
    return () => clearInterval(t);
  }, [images.length, go]);

  // Touch swipe handlers
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const badgeClass = badgeColors[item.badge] || "bg-[#173269] text-white";

  return (
    <div
      ref={cardRef}
      data-testid={`property-card-${index}`}
      className="rounded-sm overflow-hidden shadow-xl bg-[#173269] group scroll-hidden"
      style={cardInView ? { opacity: 1, transform: "translateY(0)", transitionDelay: `${index * 100}ms` } : { transitionDelay: `${index * 100}ms` }}
    >
      {/* ── Image area (swipeable) ── */}
      <div
        className="relative overflow-hidden bg-[#0A1128] select-none"
        style={{ height: "260px" }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Image */}
        <div className="absolute inset-0 transition-opacity duration-300" style={{ opacity: fade ? 1 : 0 }}>
          <img
            src={images[activeImg]}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none"
            draggable={false}
          />
        </div>

        {/* Badge */}
        {item.badge && (
          <div className="absolute top-3 right-3 z-10">
            <span className={`px-2.5 py-1 text-xs font-black rounded-sm ${badgeClass}`}>{item.badge}</span>
          </div>
        )}

        {/* Image counter */}
        {images.length > 1 && (
          <div className="absolute top-3 left-3 z-10 bg-black/50 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-sm">
            {activeImg + 1} / {images.length}
          </div>
        )}

        {/* Nav arrows (desktop hover) */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              data-testid={`card-prev-${index}`}
              className="absolute top-1/2 right-3 -translate-y-1/2 z-20 w-8 h-8 bg-white/20 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center hover:bg-[#ecc775] hover:text-[#173269] transition-all duration-200 rounded-sm opacity-0 group-hover:opacity-100"
            >
              <ChevronRight size={15} />
            </button>
            <button
              onClick={() => go(1)}
              data-testid={`card-next-${index}`}
              className="absolute top-1/2 left-3 -translate-y-1/2 z-20 w-8 h-8 bg-white/20 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center hover:bg-[#ecc775] hover:text-[#173269] transition-all duration-200 rounded-sm opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft size={15} />
            </button>
          </>
        )}
      </div>

      {/* ── Thumbnail strip — between image and info ── */}
      {images.length > 1 && (
        <div className="flex gap-1.5 px-3 py-2 bg-[#0f2050]">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              data-testid={`thumb-${index}-${i}`}
              className={`flex-shrink-0 rounded-sm overflow-hidden border-2 transition-all duration-300 ${
                i === activeImg
                  ? "border-[#ecc775] opacity-100"
                  : "border-transparent opacity-45 hover:opacity-70"
              }`}
              style={{ width: "52px", height: "38px" }}
            >
              <img src={img} alt="" className="w-full h-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}

      {/* ── Property Info ── */}
      <div className="p-5">
        <div className="flex items-center gap-1.5 text-[#ecc775] text-xs font-bold mb-2">
          <MapPin size={11} />
          <span>{item.location}</span>
        </div>
        <h3 className="text-white font-black text-lg mb-1 leading-tight">{item.title}</h3>
        <p className="text-white/75 text-sm mb-4 leading-relaxed">{item.description}</p>
        <div className="flex items-center justify-between pt-3 border-t border-white/10">
          <div>
            <p className="text-white/50 text-xs mb-0.5">السعر</p>
            <p className="text-[#ecc775] font-black text-sm">{item.price}</p>
          </div>
          <a
            href={`https://wa.me/${whatsapp || "966555661937"}?text=${encodeURIComponent(`مرحباً، أود الاستفسار عن ${item.title}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#ecc775] text-[#173269] px-4 py-2 font-black text-sm hover:bg-white transition-all duration-300 rounded-sm"
          >
            استفسر الآن <ArrowLeft size={13} />
          </a>
        </div>
      </div>
    </div>
  );
};

/* ── Main Section ── */
const Properties = ({ projects, contact }) => {
  const [titleRef, titleInView] = useInView(0.2);
  const whatsapp = contact?.whatsapp || "966555661937";

  const items = (projects?.length > 0 ? projects : defaultProjects).map(p => ({
    ...p,
    images: p.images?.length > 0 ? p.images : [p.image].filter(Boolean),
  }));

  return (
    <section id="projects" data-testid="properties-section" className="py-24 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div ref={titleRef} className={`text-center mb-14 scroll-hidden ${titleInView ? "scroll-visible" : ""}`}>
          <p className="text-[#ecc775] font-bold text-sm uppercase tracking-widest mb-3">فرص استثمارية مميزة</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#173269] mb-4">العروض العقارية</h2>
          <div className="section-divider mx-auto mb-6"></div>
          <p className="text-[#4A5568] text-lg max-w-2xl mx-auto">
            نعرض لكم أفضل الفرص الاستثمارية العقارية في جازان وأنحاء المملكة
          </p>
        </div>

        {/* ── Grid of property cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
          {items.map((item, i) => (
            <PropertyCard key={item.id || i} item={item} index={i} whatsapp={whatsapp} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default Properties;
